import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  Rating,
  Button
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { apiService } from '../utils/api';
import { normalizeImageUrl } from '../components/Post';

/**
 * ReviewsRanking - クチコミランキングページ
 */
const ReviewsRanking = () => {
  const navigate = useNavigate();
  const [ranking, setRanking] = useState([]);
  const [filteredRanking, setFilteredRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('bayesian');
  const [prefecture, setPrefecture] = useState('');
  const [prefectures, setPrefectures] = useState([]);

  useEffect(() => {
    fetchPrefectures();
    fetchRanking('');
  }, []);

  // 都道府県一覧を取得
  const fetchPrefectures = async () => {
    try {
      const res = await apiService.get('/api/areas/prefectures');
      setPrefectures(res?.data || res || []);
    } catch (e) {
      setPrefectures([]);
    }
  };

  const fetchRanking = async (pref = prefecture) => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/api/reviews-ranking', {
        params: { limit: 50, prefecture: pref || '' }
      });
      const data = response?.ranking ? response : (response?.data || {});
      const list = data.ranking || [];
      setRanking(list);
      filterAndSort(list, '', '', 'bayesian');
    } catch (err) {
      setError(err.response?.data?.error || 'ランキング取得に失敗しました');
      console.error('Ranking fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSort = (data, search, type, sort) => {
    let filtered = data;

    // タイプでフィルタ
    if (type) {
      filtered = filtered.filter((item) => item.target_type === type);
    }

    // テキストでフィルタ
    if (search.trim()) {
      const lowerSearch = search.toLowerCase();
      filtered = filtered.filter((item) =>
        (item.target_name || '').toLowerCase().includes(lowerSearch)
      );
    }

    // ソート
    if (sort === 'bayesian') {
      filtered.sort((a, b) => b.bayesian_score - a.bayesian_score);
    } else if (sort === 'rating') {
      filtered.sort((a, b) => b.avg_rating - a.avg_rating);
    } else if (sort === 'reviews') {
      filtered.sort((a, b) => b.total_reviews - a.total_reviews);
    }

    setFilteredRanking(filtered);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    filterAndSort(ranking, value, filterType, sortBy);
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilterType(value);
    filterAndSort(ranking, searchTerm, value, sortBy);
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);
    filterAndSort(ranking, searchTerm, filterType, value);
  };

  // 都道府県変更時はサーバーから再取得（エリア絞り込み）
  const handlePrefectureChange = (e) => {
    const value = e.target.value;
    setPrefecture(value);
    fetchRanking(value);
  };

  const handleViewProfile = (targetId) => {
    navigate(`/profile/${targetId}`);
  };

  const handleViewDetails = (targetId) => {
    navigate(`/reviews-ranking/${targetId}`);
  };

  const getRankMedal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* ヘッダー */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          ⭐ クチコミランキング
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Bayesian スコアに基づく信頼度調整済みランキング。直近の評価を重視し、時間経過による評価の影響を考慮しています。
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* フィルタ・検索 */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'background.default' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
          {/* 検索 */}
          <TextField
            size="small"
            placeholder="企業名やユーザー名で検索..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />

          {/* エリア（都道府県） */}
          <FormControl size="small">
            <InputLabel>エリア</InputLabel>
            <Select
              value={prefecture}
              onChange={handlePrefectureChange}
              label="エリア"
            >
              <MenuItem value="">全国</MenuItem>
              {prefectures.map((p) => (
                <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* フィルタ */}
          <FormControl size="small">
            <InputLabel>タイプ</InputLabel>
            <Select
              value={filterType}
              onChange={handleFilterChange}
              label="タイプ"
            >
              <MenuItem value="">すべて</MenuItem>
              <MenuItem value="corporate">企業</MenuItem>
              <MenuItem value="poster">投稿ユーザー</MenuItem>
            </Select>
          </FormControl>

          {/* ソート */}
          <FormControl size="small">
            <InputLabel>ソート</InputLabel>
            <Select
              value={sortBy}
              onChange={handleSortChange}
              label="ソート"
            >
              <MenuItem value="bayesian">Bayesian スコア (高)</MenuItem>
              <MenuItem value="rating">平均評価 (高)</MenuItem>
              <MenuItem value="reviews">クチコミ数 (多)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* ランキングテーブル（スマホでは横スクロール） */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead sx={{ backgroundColor: 'primary.light' }}>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 700, width: 60 }}>
                順位
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>名前</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>評価</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                Bayesian スコア
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                クチコミ数
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>
                3ヶ月内
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: 100 }}>
                アクション
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRanking.length > 0 ? (
              filteredRanking.map((item, index) => (
                <TableRow
                  key={item.target_id}
                  sx={{
                    backgroundColor: index < 3 ? 'action.hover' : 'transparent',
                    '&:hover': { backgroundColor: 'action.selected' }
                  }}
                >
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: 18 }}>
                    {getRankMedal(item.rank)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={normalizeImageUrl(item.target_avatar)}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.target_name}
                        </Typography>
                        <Chip
                          label={item.target_type === 'corporate' ? '企業' : '投稿ユーザー'}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <Rating
                        value={item.avg_rating}
                        readOnly
                        size="small"
                        precision={0.1}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {item.avg_rating.toFixed(1)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                        color: item.bayesian_score >= 4 ? 'success.main' : 'text.primary'
                      }}
                    >
                      {item.bayesian_score.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.total_reviews}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={item.recent_reviews_3m}
                      size="small"
                      variant="outlined"
                      color={item.recent_reviews_3m > 0 ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleViewDetails(item.target_id)}
                      >
                        詳細
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleViewProfile(item.target_id)}
                      >
                        プロフィール
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    該当するランキングがありません
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 統計情報 */}
      <Paper sx={{ p: 3, mt: 3, backgroundColor: 'info.light' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          📊 ランキング情報
        </Typography>
        <Typography variant="body2" color="text.secondary">
          表示件数: {filteredRanking.length} / {ranking.length}件
        </Typography>
      </Paper>
    </Container>
  );
};

export default ReviewsRanking;
